import { useEffect, useMemo, useState } from "react";
import "../styles/PageToolbarSearchFilter.css";
import SavedFiltersPanel from "./SavedFiltersPanel";

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export default function ChoiceFilterPopover({
  open,
  title = "Filter",
  fields = [],
  appliedFilters = {},
  filterMode = "all",
  onApply,
  onClose,
  module = null,
}) {
  const [draft, setDraft] = useState({});
  const [mode, setMode] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [fieldSearch, setFieldSearch] = useState({});

  useEffect(() => {
    if (!open) return;
    setDraft(JSON.parse(JSON.stringify(appliedFilters || {})));
    setMode(filterMode || "all");
    setSearch("");
    setExpanded(null);
    setFieldSearch({});
  }, [open, appliedFilters, filterMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  const normalizedFields = useMemo(() => {
    return (fields || []).map((f) => {
      if (f.type === "daterange") return f;
      return {
        ...f,
        options: (f.options || [])
          .filter((o) => o && String(o.value || "").trim() && String(o.label || "").trim())
          .map((o) => ({ value: String(o.value), label: String(o.label) }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      };
    });
  }, [fields]);

  const visibleFields = search.trim()
    ? normalizedFields.filter((f) => f.label.toLowerCase().includes(search.toLowerCase()))
    : normalizedFields;

  const setOp = (key, op) => setDraft((d) => ({ ...d, [key]: { op, values: d[key]?.values || [] } }));

  const toggleVal = (key, val) => {
    setDraft((d) => {
      const curr = d[key]?.values || [];
      const next = curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val];
      return { ...d, [key]: { op: d[key]?.op || "is", values: next } };
    });
  };

  const clearField = (key) => {
    setDraft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  };

  const handleReset = () => {
    setDraft({});
    setMode("all");
  };

  const handleApply = () => {
    onApply?.(draft, mode);
    onClose?.();
  };

  if (!open) return null;

  return (
    <>
      <div className="cmn-fdr-scrim" onClick={onClose} />
      <div className="cmn-fdr">
        <div className="cmn-fdr-header">
          <span className="cmn-fdr-title">{title}</span>
          <button className="cmn-fdr-reset" type="button" onClick={handleReset}>Reset</button>
        </div>

        {module && (
          <div className="cmn-fdr-saved-wrap">
            <SavedFiltersPanel
              module={module}
              appliedFilters={draft}
              filterMode={mode}
              onApply={(filterDef, fm) => {
                setDraft(JSON.parse(JSON.stringify(filterDef)));
                setMode(fm || "all");
                onApply?.(filterDef, fm || "all");
                onClose?.();
              }}
            />
          </div>
        )}

        <div className="cmn-fdr-search-wrap">
          <SearchIcon />
          <input
            className="cmn-fdr-search"
            type="text"
            placeholder="Filter Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="cmn-fdr-list">
          {visibleFields.map((field) => {
            const isOpen = expanded === field.key;

            if (field.type === "daterange") {
              const curr = draft[field.key] || { type: "daterange", from: "", to: "" };
              const hasActive = !!(curr.from || curr.to);
              // Manually editing From/To clears presetKey — the exact range
              // no longer necessarily matches a named preset once hand-edited.
              const setDate = (part, val) =>
                setDraft((d) => ({ ...d, [field.key]: { type: "daterange", ...d[field.key], [part]: val, presetKey: "" } }));
              const applyPreset = (preset) =>
                setDraft((d) => ({ ...d, [field.key]: { type: "daterange", ...preset.range(), presetKey: preset.key } }));
              return (
                <div key={field.key} className="cmn-fdr-field">
                  <button
                    className="cmn-fdr-field-hdr"
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : field.key)}
                  >
                    <span className="cmn-fdr-field-name">
                      {field.label}
                      {hasActive && <span className="cmn-fdr-badge">✓</span>}
                    </span>
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      {isOpen ? <polyline points="18 15 12 9 6 15" /> : <polyline points="9 18 15 12 9 6" />}
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="cmn-fdr-field-body">
                      {/* Optional quick-range presets (e.g. "Last Month") — a
                          generic capability any daterange field can opt into
                          via field.presets, so it isn't a one-off bolted onto
                          a single page's filter drawer. */}
                      {field.presets?.length > 0 && (
                        <div className="cmn-fdr-preset-opts">
                          {field.presets.map((preset) => (
                            <button
                              key={preset.key}
                              type="button"
                              className={`cmn-fdr-preset-btn${curr.presetKey === preset.key ? " cmn-fdr-preset-btn--active" : ""}`}
                              onClick={() => applyPreset(preset)}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="cmn-fdr-daterange">
                        <label className="cmn-fdr-daterange-label">From</label>
                        <input
                          type="date"
                          className="cmn-fdr-date-input"
                          value={curr.from || ""}
                          onChange={(e) => setDate("from", e.target.value)}
                        />
                        <label className="cmn-fdr-daterange-label">To</label>
                        <input
                          type="date"
                          className="cmn-fdr-date-input"
                          value={curr.to || ""}
                          onChange={(e) => setDate("to", e.target.value)}
                        />
                      </div>
                      {hasActive && (
                        <button type="button" className="cmn-fdr-field-reset" onClick={() => clearField(field.key)}>
                          Reset this filter
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            const curr = draft[field.key] || { op: "is", values: [] };
            const fs = fieldSearch[field.key] || "";
            // Alphabetical (field.options already sorted this way, see
            // normalizedFields above) with currently-selected values pinned
            // to the top — recomputed live off `curr.values` so checking an
            // option moves it up immediately rather than only reordering
            // the next time this field is expanded.
            const opts = (fs.trim()
              ? field.options.filter((o) => o.label.toLowerCase().includes(fs.toLowerCase()))
              : field.options
            ).slice().sort((a, b) => {
              const aSel = curr.values?.includes(a.value);
              const bSel = curr.values?.includes(b.value);
              if (aSel !== bSel) return aSel ? -1 : 1;
              return a.label.localeCompare(b.label);
            });
            const hasActive = curr.values?.length > 0;

            return (
              <div key={field.key} className="cmn-fdr-field">
                <button
                  className="cmn-fdr-field-hdr"
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : field.key)}
                >
                  <span className="cmn-fdr-field-name">
                    {field.label}
                    {hasActive && <span className="cmn-fdr-badge">{curr.values.length}</span>}
                  </span>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    {isOpen
                      ? <polyline points="18 15 12 9 6 15" />
                      : <polyline points="9 18 15 12 9 6" />
                    }
                  </svg>
                </button>

                {isOpen && (
                  <div className="cmn-fdr-field-body">
                    <div className="cmn-fdr-op-row">
                      <select className="cmn-fdr-op" value={curr.op || "is"} onChange={(e) => setOp(field.key, e.target.value)}>
                        <option value="is">Is</option>
                        <option value="is_not">Is Not</option>
                      </select>
                      {hasActive && (
                        <button type="button" className="cmn-fdr-field-reset" onClick={() => clearField(field.key)}>
                          Reset this filter
                        </button>
                      )}
                    </div>

                    {field.options.length > 5 && (
                      <div className="cmn-fdr-val-search-wrap">
                        <SearchIcon />
                        <input
                          className="cmn-fdr-val-search"
                          type="text"
                          placeholder="Search..."
                          value={fs}
                          onChange={(e) => setFieldSearch((s) => ({ ...s, [field.key]: e.target.value }))}
                        />
                      </div>
                    )}

                    <div className="cmn-fdr-opts">
                      {opts.map((opt) => (
                        <label key={opt.value} className="cmn-fdr-opt">
                          <input
                            type="checkbox"
                            checked={curr.values.includes(opt.value)}
                            onChange={() => toggleVal(field.key, opt.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                      {opts.length === 0 && <div className="cmn-fdr-empty">No options</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="cmn-fdr-mode">
          <label className="cmn-fdr-mode-opt">
            <input type="radio" name="cmnFdrMode" value="any" checked={mode === "any"} onChange={() => setMode("any")} />
            Any of these
          </label>
          <label className="cmn-fdr-mode-opt">
            <input type="radio" name="cmnFdrMode" value="all" checked={mode === "all"} onChange={() => setMode("all")} />
            All of these
          </label>
        </div>

        <div className="cmn-fdr-footer">
          <button className="cmn-fdr-find" type="button" onClick={handleApply}>Find</button>
          <button className="cmn-fdr-cancel" type="button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}
